@if (loading) {
    <spinner-overlay [fullscreen]="true"></spinner-overlay>
}

<!-- the guide card, shared by the small and large screen layouts -->
<ng-template #guide>
    @if (user?.role===USER_ROLE.GUEST) {
        <nav class="guest-bar" aria-label="LINQ links">
            <a class="guest-link" [href]="websiteUrl" target="_blank" rel="noopener">
                <mat-icon aria-hidden="true">arrow_back</mat-icon><span>Website</span>
            </a>
            <span class="spacer"></span>
            <a class="guest-link" [href]="demoBookingUrl" target="_blank" rel="noopener">
                <mat-icon aria-hidden="true">event</mat-icon><span>Book a demo</span>
            </a>
            <button mat-flat-button color="primary" class="try-button" (click)="register()" matTooltip="Register for LINQ so I can try it myself">Try LINQ</button>
        </nav>
    }

    <section class="guide-card" aria-labelledby="guide-title">
        <header class="guide-header">
            <div class="guide-eyebrow">
                @if (contextName) {
                    <span class="model-name truncated-name" [title]="contextName">{{contextName}}</span>
                    <span class="dot" aria-hidden="true">·</span>
                }
                <span>{{eyebrow}}</span>
            </div>

            <ol class="stage-progress" [attr.aria-label]="'Stage ' + (phaseIndex + 1) + ' of ' + phases.length">
                @for (phase of phases; track phase.label; let i = $index) {
                    <li [class.done]="i < phaseIndex" [class.current]="i === phaseIndex" [attr.aria-current]="i === phaseIndex ? 'step' : null">
                        <span class="bar"></span>
                        <span class="label" [title]="phase.label">{{phase.short}}</span>
                    </li>
                }
            </ol>

            <h2 id="guide-title" class="guide-title">{{title}}</h2>
        </header>

        <div class="guide-messages" aria-live="polite">
            @for (m of safeMessages; track $index) {
                <p class="guide-message" [innerHTML]="m"></p>
            }
        </div>

        @if (stage === INTRODUCTION_STAGE.ANIMATION && simulation) {
            <div class="simulation" aria-label="Simulation progress">
                <div class="simulation-time">
                    <mat-icon aria-hidden="true">schedule</mat-icon>
                    <span>
                        Day {{simulation.day}} of {{simulation.days}} <span class="muted">· {{simulation.time}}</span>
                        @if (simulationFinished) { <span class="muted">· year complete</span> }
                    </span>
                    <span class="spacer"></span>
                    <button mat-icon-button (click)="toggleSimulation()" [disabled]="simulationFinished"
                        [matTooltip]="simulationRunning ? 'Pause the simulation' : 'Carry on the simulation'"
                        [attr.aria-label]="simulationRunning ? 'Pause the simulation' : 'Carry on the simulation'">
                        <mat-icon>{{simulationRunning ? 'pause' : 'play_arrow'}}</mat-icon>
                    </button>
                </div>
                <div class="simulation-bar" aria-hidden="true">
                    <span [style.width.%]="simulation.percent"></span>
                </div>
                <dl class="simulation-stats">
                    <div><dt>Received</dt><dd>{{simulation.received | number}}</dd></div>
                    <div><dt>Completed</dt><dd>{{simulation.completed | number}}</dd></div>
                    <div><dt>Waiting</dt><dd>{{simulation.queued | number}}</dd></div>
                </dl>
            </div>
        }

        @if (stage === INTRODUCTION_STAGE.FINISH && user?.role===USER_ROLE.GUEST) {
            <div class="try-linq">
                <h3>Model your own processes</h3>
                <p>LINQ is free for an organisation with a single user and up to 150 nodes, so you can try it without obligation.</p>
                <div class="try-linq-actions">
                    <button mat-flat-button color="primary" (click)="register()">Try LINQ for free</button>
                    <a mat-stroked-button [href]="demoBookingUrl" target="_blank" rel="noopener">Book a demo</a>
                </div>
            </div>
        }@else if (stage === INTRODUCTION_STAGE.FINISH && inviteToCreate) {
            <div class="try-linq">
                <h3>Model your own organisation</h3>
                <p>Tell us about a process in your business and AI will build a model of it, ready to walk through like this one.</p>
                <div class="try-linq-actions">
                    <button mat-flat-button color="primary" (click)="createOwnOrganisation()" data-testid="walkthrough-create-organisation">Create your organisation</button>
                </div>
            </div>
        }

        @if (stage === INTRODUCTION_STAGE.ORGANISATION) {
            <!-- the same choice as double-clicking a model, for touch screens and keyboards -->
            <nav class="model-choices" aria-label="Choose a model">
                @for (model of organisationModels; track model._key) {
                    <button type="button" class="model-choice" (click)="chooseModel(model)" [disabled]="navigating">
                        <mat-icon aria-hidden="true">account_tree</mat-icon>
                        <span class="truncated-name" [title]="model.name">{{model.name}}</span>
                    </button>
                }
            </nav>
        }@else {
        <footer class="guide-actions">
            <button mat-icon-button (click)="restart()" [disabled]="navigating" [matTooltip]="startsAtOrganisation ? 'Start again or choose another model' : 'Start again'" aria-label="Restart">
                <mat-icon>replay</mat-icon>
            </button>
            <span class="spacer"></span>
            @if (stage < INTRODUCTION_STAGE.ANIMATION) {
                <button mat-button color="accent" (click)="completeModel()" [disabled]="navigating" matTooltip="Skip the step through and show the whole model">Skip to end</button>
            }
            <button mat-stroked-button (click)="back()" [disabled]="!canGoBack" matTooltip="Go back a step (←)">
                <mat-icon>arrow_back</mat-icon>Back
            </button>
            @if (stage !== INTRODUCTION_STAGE.FINISH) {
                <button mat-flat-button color="accent" class="next-button" (click)="loadNextStage()" [disabled]="navigating || (stage === INTRODUCTION_STAGE.EPM && !epmReady)" matTooltip="Keyboard: →">
                    {{nextLabel}}<mat-icon iconPositionEnd>arrow_forward</mat-icon>
                </button>
            }
        </footer>
        }
    </section>

    <!-- how to read the model, beside the steps that walk through it -->
    @if (isWalkthrough) {
        <section class="model-key" aria-labelledby="model-key-title">
            <h3 id="model-key-title" class="section-heading">Reading the model</h3>
            <img src="assets/images/LINQ-Language-2024.webp" alt="The kinds of node in a LINQ model and how they connect: actions create information, roles and systems do the actions, and outcomes are what the process achieves">
            <p>A model is a set of connected nodes: the <b>actions</b> taken, the <b>information</b> they create and use, and the <b>roles</b> and <b>systems</b> that do the work. Each line is a relationship, such as an action creating information or a role using a system.</p>
            <p class="muted">{{app.smallScreen ? 'Pinch to zoom the model and drag to move it.' : 'Scroll to zoom the model and drag to move it.'}}</p>
        </section>
    }
</ng-template>

<!-- the EPM report, shown in place of the graph. It is kept once built, hidden at other stages -->
<ng-template #epm>
    @if (epmLinqset) {
        <div class="epm-report" #epmReport [hidden]="stage !== INTRODUCTION_STAGE.EPM">
            <app-epm-scorecard-tab [linqset]="epmLinqset" [readOnly]="true"></app-epm-scorecard-tab>
        </div>
    }
</ng-template>

<!-- the report has its own figures, so the insights step aside while it shows -->
<ng-template #insights>
    @if (stage >= INTRODUCTION_STAGE.ANIMATION && stage !== INTRODUCTION_STAGE.EPM) {
        <section class="insights-section">
            <h3 class="section-heading">Model insights</h3>
            <app-insights-overview [linqset]="linqset" [summary]="true" [comparison]="stage===INTRODUCTION_STAGE.PROJECT&&this.comparison"/>
        </section>
    }
</ng-template>

@if (app.smallScreen) {
    @if (linqset && stage > INTRODUCTION_STAGE.INITIAL) {
        <div class="guide-panel small">
            <ng-container *ngTemplateOutlet="guide"></ng-container>
        </div>
    }
    <div class="graph-container-small">
        <app-graph-component [linqset]="linqset" [modelType]="modelType" [readOnly]="true" (itemDoubleClickedEvent)="handleGraphDoubleClick($event)" (graphReady)="handleGraphReady($event)" cdkOverlayOrigin #trigger="cdkOverlayOrigin"></app-graph-component>
        <ng-container *ngTemplateOutlet="epm"></ng-container>
    </div>
    <div class="guide-panel small">
        <ng-container *ngTemplateOutlet="insights"></ng-container>
    </div>

}@else {
    @if (user?.role!==USER_ROLE.GUEST) {
        <div id="menu" >
            <app-menu [screen]="screen" [linqset]="linqset" [organisation]="linqset?.organisation" (menuEvent)="handleFunctionEvent($event)" ></app-menu>
        </div>
    }

    <mat-sidenav-container [ngClass]="user?.role===USER_ROLE.GUEST?'drawer-container-guest':'drawer-container-user'" autosize="true">
        <mat-sidenav #leftdrawer class="guide-drawer" mode="side" position="start" [style.width.px]="480" [opened]="false">
            @if (linqset && stage > INTRODUCTION_STAGE.INITIAL) {
                <div class="guide-panel">
                    <div class="guide-sticky">
                        <ng-container *ngTemplateOutlet="guide"></ng-container>
                    </div>
                    <ng-container *ngTemplateOutlet="insights"></ng-container>
                </div>
            }
        </mat-sidenav>
        <mat-sidenav-content>
            <app-graph-component [linqset]="linqset" [modelType]="modelType" [readOnly]="true" (itemDoubleClickedEvent)="handleGraphDoubleClick($event)" (graphReady)="handleGraphReady($event)" cdkOverlayOrigin #trigger="cdkOverlayOrigin"></app-graph-component>
            <ng-container *ngTemplateOutlet="epm"></ng-container>
        </mat-sidenav-content>
    </mat-sidenav-container>
}
